<template>
  <b-modal
    ref="modal"
    :id="modalId"
    :title="title"
    ok-title="Use image"
    ok-variant="primary"
    :ok-disabled="!hasSelection || !!error"
    size="lg"
    body-class="p-0"
    centered
    @hidden="onModalHidden"
  >
    <div class="px-5 py-4 pb-5 modal-body-content">
      <image-file-input
        class="m-0"
        :id="`${modalId}-file-input`"
        :file="pendingFile || file"
        upload-text="Drag and Drop or <u>Click to upload</u>"
        :supported-text="supportedText"
        @change="onInputChange"
      />
      <small v-if="error" class="text-danger">{{ error }}</small>
      <div class="mt-3 d-flex flex-column align-items-center" v-if="pendingPreviewUrl || previewUrl">
        <div class="label mb-2">Preview</div>
        <div :class="previewClass">
          <img :src="pendingPreviewUrl || previewUrl" alt="Selected image preview" class="modal-preview-image" />
        </div>
      </div>
    </div>

    <template #modal-footer>
      <b-button variant="outline-primary" @click="onCancel">Cancel</b-button>
      <b-button variant="primary" @click="confirm">Use image</b-button>
    </template>
  </b-modal>
</template>

<script setup>
import { computed, ref } from 'vue';
import ImageFileInput from '@/pages/recognition/components/ImageFileInput.vue';

const props = defineProps({
  modalId: { type: String, required: true },
  title: { type: String, default: 'Upload image' },
  acceptedFileTypes: { type: Array, default: () => ['image/jpeg', 'image/png'] },
  maxSizeBytes: { type: Number, default: 10 * 1024 * 1024 },
  file: { type: [Object, File], default: null },
  previewUrl: { type: String, default: '' },
  previewType: { type: String, default: 'banner' },
  supportedText: { type: String, default: undefined },
});

const emit = defineEmits(['update:file', 'update:previewUrl']);

const pendingFile = ref(null);
const pendingPreviewUrl = ref('');
const error = ref('');

const hasSelection = computed(() => Boolean(pendingFile.value || props.file));
const modal = ref(null);
const previewClass = computed(() => (props.previewType === 'badge' ? 'badge-preview' : 'banner-preview'));

const revokeObjectUrl = url => {
  if (url) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }
};

const resetState = () => {
  error.value = '';
  if (pendingPreviewUrl.value) {
    revokeObjectUrl(pendingPreviewUrl.value);
  }
  pendingFile.value = null;
  pendingPreviewUrl.value = '';
};

const onModalHidden = () => {
  resetState();
};

const onCancel = () => {
  modal.value?.hide();
};

const onInputChange = event => {
  const file = event?.target?.files?.[0] || event?.dataTransfer?.files?.[0];
  if (!file) {
    return;
  }
  if (file.size > props.maxSizeBytes) {
    error.value = 'Image must be 10MB or less.';
    return;
  }
  if (file.type && !props.acceptedFileTypes.includes(file.type)) {
    error.value = 'Only JPG or PNG images are allowed.';
    return;
  }
  if (pendingPreviewUrl.value) {
    revokeObjectUrl(pendingPreviewUrl.value);
  }
  pendingFile.value = file;
  pendingPreviewUrl.value = URL.createObjectURL(file);
  error.value = '';
};

const confirm = () => {
  if (!pendingFile.value) {
    return;
  }
  if (props.previewUrl) {
    revokeObjectUrl(props.previewUrl);
  }
  emit('update:file', pendingFile.value);
  emit('update:previewUrl', pendingPreviewUrl.value);
  pendingFile.value = null;
  pendingPreviewUrl.value = '';

  modal.value?.hide();
};
</script>

<style scoped>
:deep(.modal-title) {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  width: 100%;
  text-align: center;
  margin-left: 1.25rem;
}

:deep(.modal-header) {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.modal-body-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.modal-preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

:deep(.image-file-input__label) {
  margin: 0;
}

.label {
  font-weight: 500;
  font-size: 1rem;
  line-height: 1.5rem;
  color: var(--palette-color-default);
}

.banner-preview {
  width: 16rem;
  min-height: 4rem;
  background: var(--palette-color-core-1-lighten-90);
  border: 1px dashed var(--palette-color-default-lighten-50);
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.banner-preview .modal-preview-image {
  height: auto;
  object-fit: unset;
}

.badge-preview {
  height: 9rem;
  width: 9rem;
  background: var(--palette-color-core-1-lighten-90);
  border: 1px dashed var(--palette-color-default-lighten-50);
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
</style>
